import { Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { motion } from "framer-motion";

const mainServices = [
  { id: "court-reporter", label: "Court Reporter" },
  { id: "trial-readiness", label: "Trial Readiness" },
  { id: "trial-tech", label: "Trial Tech" },
  { id: "exhibit-tech", label: "Exhibit Tech" },
  { id: "videographer", label: "Videographer" },
  { id: "interpreter", label: "Interpreter" },
  { id: "other-service", label: "Other" },
];

const courtReportingOptions = [
  { id: "realtime", label: "Real-Time" },
  { id: "rough-drafts", label: "Rough Drafts" },
  { id: "expedites", label: "Expedites" },
  { id: "video-syncing", label: "Video Syncing" },
];

const interpreterLanguages = [
  "Spanish", "Mandarin Chinese", "Arabic", "French", "Portuguese",
  "Korean", "Vietnamese", "Russian", "Japanese", "Haitian Creole",
  "Hindi", "Tagalog", "German", "Italian", "Polish",
  "Cantonese", "Farsi/Persian", "Urdu", "ASL (American Sign Language)",
];

function CheckboxItem({ checked, onClick, label, testId, size = "default" }) {
  const padding = size === "small" ? "p-3" : "p-4";
  const textSize = size === "small" ? "text-xs" : "text-sm";
  return (
    <div
      className={`flex items-center gap-${size === "small" ? "2" : "3"} ${padding} border cursor-pointer transition-all ${
        checked ? "border-[#38b6ff] bg-[#38b6ff]/10" : "border-[#333333] hover:border-[#38b6ff]/50"
      }`}
      onClick={onClick}
      data-testid={testId}
    >
      <div className={`w-4 h-4 border flex items-center justify-center flex-shrink-0 transition-colors ${
        checked ? "bg-[#38b6ff] border-[#38b6ff]" : "border-[#333333]"
      }`}>
        {checked && <Check className="w-3 h-3 text-white" />}
      </div>
      <span className={`text-[#F5F5F7] ${textSize}`}>{label}</span>
    </div>
  );
}

export default function ServiceSelector({
  selectedServices, onServiceToggle,
  selectedCourtOptions, onCourtOptionToggle,
  selectedLanguages, onLanguageToggle,
  otherServiceText, onOtherServiceChange,
  otherLanguageText, onOtherLanguageChange,
}) {
  const showCourtReportingOptions = selectedServices.includes("court-reporter");
  const showInterpreterLanguages = selectedServices.includes("interpreter");
  const showOtherServiceInput = selectedServices.includes("other-service");

  return (
    <>
      {/* Services Selection */}
      <div>
        <Label className="text-[#F5F5F7] text-sm mb-4 block" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
          Select Services *
        </Label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {mainServices.map((service) => (
            <CheckboxItem
              key={service.id}
              checked={selectedServices.includes(service.id)}
              onClick={() => onServiceToggle(service.id)}
              label={service.label}
              testId={`service-${service.id}`}
            />
          ))}
        </div>
      </div>

      {/* Court Reporting Specific Options */}
      {showCourtReportingOptions && (
        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="border-l-2 border-[#38b6ff] pl-6">
          <Label className="text-[#38b6ff] text-sm mb-4 block" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
            Court Reporter Options
          </Label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {courtReportingOptions.map((option) => (
              <CheckboxItem
                key={option.id}
                checked={selectedCourtOptions.includes(option.id)}
                onClick={() => onCourtOptionToggle(option.id)}
                label={option.label}
                testId={`court-option-${option.id}`}
                size="small"
              />
            ))}
          </div>
        </motion.div>
      )}

      {/* Interpreter Language Selection */}
      {showInterpreterLanguages && (
        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="border-l-2 border-[#38b6ff] pl-6" data-testid="interpreter-languages">
          <Label className="text-[#38b6ff] text-sm mb-4 block" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
            Language(s) Needed *
          </Label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {interpreterLanguages.map((language) => (
              <CheckboxItem
                key={language}
                checked={selectedLanguages.includes(language)}
                onClick={() => onLanguageToggle(language)}
                label={language}
                testId={`lang-${language.toLowerCase().replace(/[\s/()]/g, '-')}`}
                size="small"
              />
            ))}
            <div className={`flex items-center gap-2 p-3 border cursor-pointer transition-all ${
              otherLanguageText.length > 0 ? "border-[#38b6ff] bg-[#38b6ff]/10" : "border-[#333333] hover:border-[#38b6ff]/50"
            }`} data-testid="lang-other">
              <span className="text-[#F5F5F7] text-xs">Other</span>
            </div>
          </div>
          <div className="mt-3">
            <Input
              type="text" placeholder="Other language(s)..." value={otherLanguageText}
              onChange={(e) => onOtherLanguageChange(e.target.value)}
              className="rounded-none bg-[#1A1A1A] border-[#333333] text-[#F5F5F7] placeholder:text-[#E0E0E0]/50 focus:border-[#38b6ff] focus:ring-1 focus:ring-[#38b6ff]"
              data-testid="other-language-input"
            />
          </div>
        </motion.div>
      )}

      {/* Other Service Input */}
      {showOtherServiceInput && (
        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="border-l-2 border-[#38b6ff] pl-6" data-testid="other-service-input-section">
          <Label className="text-[#38b6ff] text-sm mb-4 block" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
            Please specify *
          </Label>
          <Input
            type="text" placeholder="Describe the service you need..." value={otherServiceText}
            onChange={(e) => onOtherServiceChange(e.target.value)}
            className="rounded-none bg-[#1A1A1A] border-[#333333] text-[#F5F5F7] placeholder:text-[#E0E0E0]/50 focus:border-[#38b6ff] focus:ring-1 focus:ring-[#38b6ff]"
            data-testid="other-service-input"
          />
        </motion.div>
      )}
    </>
  );
}
